import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { auth } from "./firebase";
import { useNavigate } from 'react-router-dom';
import config_names from "./config_names";
import { 
  FiPlus, 
  FiLoader,
  FiFileText, 
  FiTrash2,
  FiCheckCircle,
  FiUser,
  FiUpload,
  FiFile
} from "react-icons/fi";
import QuotaDisplay from "./QuotaDisplay"; // Import the new component
const CONFIG = require("./config");

function normalizeSkillList(skills) {
  if (Array.isArray(skills)) {
    return skills
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .slice(0, 5);
  }

  if (typeof skills === "string") {
    const trimmed = skills.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => String(item || "").trim())
          .filter(Boolean)
          .slice(0, 5);
      }
    } catch (_error) {
      return trimmed
        .split(/[,;\n]+/)
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 5);
    }
  }

  return [];
}

function toSingleSentence(text) {
  const normalized = String(text || "").replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "No summary available.";
  }

  const noEllipsis = normalized.replace(/\.{3,}\s*$/, "").trim();
  const sentence = noEllipsis.split(/(?<=[.!?])\s+/)[0] || noEllipsis;
  if (/[.!?]$/.test(sentence)) {
    return sentence;
  }
  return `${sentence}.`;
}

function normalizeCandidateName(value) {
  if (value === null || value === undefined) {
    return "";
  }

  const text = String(value).trim().replace(/\s+/g, " ");
  if (!text) {
    return "";
  }

  const normalized = text.toLowerCase().replace(/[\s\-_]+/g, " ").trim();
  const compact = normalized.replace(/[^a-z0-9]+/g, "");
  const unknownValues = new Set([
    "unknown",
    "none",
    "non",
    "n/a",
    "na",
    "null",
    "unknown candidate",
    "not sure",
    "unsure",
    "uncertain",
    "cannot determine",
    "cant determine",
    "not provided",
    "no name",
    "no candidate name",
  ]);
  const unknownCompact = new Set([
    "unknown",
    "none",
    "non",
    "na",
    "null",
    "unknowncandidate",
    "notsure",
    "unsure",
    "uncertain",
    "cannotdetermine",
    "cantdetermine",
    "notprovided",
    "noname",
    "nocandidatename",
  ]);

  if (unknownValues.has(normalized) || unknownCompact.has(compact)) {
    return "";
  }

  return text;
}

function Overview() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createMode, setCreateMode] = useState(false);
  const [newJobTitle, setNewJobTitle] = useState("");
  const [newJobDescription, setNewJobDescription] = useState("");
  const [uploadingResumes, setUploadingResumes] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [step, setStep] = useState(1); // Step 1: Job Description, Step 2: Upload Resumes
  const [isDragging, setIsDragging] = useState(false); // New state for drag feedback
  const pollingIntervalRef = useRef(null);
  const POLLING_INTERVAL = 10000; // 10 seconds
  
  // Drag and drop event handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadResumes(files);
    }
  };
  
  const showToastMessage = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  }, []);
  
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${CONFIG.API_URL}/jobs`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch jobs");
      }
      
      const data = await response.json();
      setJobs(data);
      
      // Select the first job by default if available
      if (data.length > 0 && !selectedJob) {
        setSelectedJob(data[0].id);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setLoading(false);
      showToastMessage("Error loading jobs");
    }
  }, [selectedJob, showToastMessage]);
  
  const fetchJobDetails = useCallback(async (jobId, showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) {
        setLoading(true);
      }
      
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${CONFIG.API_URL}/job/${jobId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch job details");
      }
      
      const data = await response.json();
      setJobDetails(data);
      
      if (showLoadingSpinner) {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      if (showLoadingSpinner) {
        setLoading(false);
      }
      showToastMessage("Error loading job details");
    }
  }, [showToastMessage]);
  
  useEffect(() => {
    if (currentUser) {
      fetchJobs();
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [currentUser, fetchJobs]);
  
  useEffect(() => {
    if (selectedJob) {
      fetchJobDetails(selectedJob);
    }
  }, [selectedJob, fetchJobDetails]);

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    const hasProcessingResumes = jobDetails?.resumes?.some(resume => 
      resume.status === 'processing'
    );
    
    if (hasProcessingResumes && selectedJob) {
      pollingIntervalRef.current = setInterval(() => {
        fetchJobDetails(selectedJob, false);
      }, POLLING_INTERVAL);
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [jobDetails, selectedJob, fetchJobDetails]);

  const createJob = async () => {
    try {
      if (!newJobTitle.trim() || !newJobDescription.trim()) {
        showToastMessage("Please provide both a title and description");
        return;
      }
      
      if (!currentUser) {
        showToastMessage("You must be logged in to create a job");
        return;
      }
      
      const token = await auth.currentUser.getIdToken(true);
      const response = await fetch(`${CONFIG.API_URL}/job`, {
        method: "POST",
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newJobTitle,
          description: newJobDescription
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create job");
      }
      
      const newJob = await response.json();
      setJobs(prev => [newJob, ...prev]);
      setSelectedJob(newJob.id);
      setStep(2);
      showToastMessage("Job created successfully! Now upload resumes.");
    } catch (error) {
      console.error("Error creating job:", error);
      showToastMessage(error.message || "Error creating job");
    }
  };
  
  const uploadResumes = async (files) => {
    try {
      setUploadingResumes(true);
      const token = await auth.currentUser.getIdToken();
      const formData = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formData.append("file", files[i]);
      }
      
      const response = await fetch(`${CONFIG.API_URL}/resume/${selectedJob}`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to upload resumes");
      }
      
      await response.json();
      
      fetchJobDetails(selectedJob);
      setCreateMode(false);
      setStep(1);
      setUploadingResumes(false);
      setNewJobTitle("");
      setNewJobDescription("");
      
      showToastMessage(`${files.length} resume(s) uploaded successfully!`);
    } catch (error) {
      console.error("Error uploading resumes:", error);
      setUploadingResumes(false);
      showToastMessage(error.message || "Error uploading resumes");
    }
  };
  
  const deleteJob = async (jobId) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${CONFIG.API_URL}/job/${jobId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error("Failed to delete job");
      }
      
      setJobs(prev => prev.filter(job => job.id !== jobId));
      
      if (selectedJob === jobId) {
        const remainingJobs = jobs.filter(job => job.id !== jobId);
        if (remainingJobs.length > 0) {
          setSelectedJob(remainingJobs[0].id);
        } else {
          setSelectedJob(null);
          setJobDetails(null);
        }
      }
      
      showToastMessage("Job deleted successfully");
    } catch (error) {
      console.error("Error deleting job:", error);
      showToastMessage("Error deleting job");
    }
  };
  
  const deleteResume = async (resumeId) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${CONFIG.API_URL}/resume/${resumeId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error("Failed to delete resume");
      }
      
      fetchJobDetails(selectedJob);
      showToastMessage("Resume deleted successfully");
    } catch (error) {
      console.error("Error deleting resume:", error);
      showToastMessage("Error deleting resume");
    }
  };
  
  const viewResume = (resumeId) => {
    navigate(`/dashboard/resume/${resumeId}`);
  };
  
  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      uploadResumes(files);
    }
  };
  
  const startCreateJob = () => {
    setCreateMode(true);
    setStep(1);
    setNewJobTitle("");
    setNewJobDescription("");
  };
  
  const cancelCreateJob = () => {
    setCreateMode(false);
    setStep(1);
    setNewJobTitle("");
    setNewJobDescription("");
  };
  
  if (!currentUser) {
    return (
      <div className="login-prompt">
        <h2>Please log in to access your dashboard</h2>
        <p>You need to be logged in to view and manage your job matches.</p>
        <a href="/app/login" className="primary-button">Log In</a>
      </div>
    );
  }
  
  return (
    <div className="overview-container">
      {/* Quota display at the top */}
      <QuotaDisplay />

      {/* Wrap the sidebar + main content in a flex container */}
      <div className="overview-main">
        
        {/* Sidebar with jobs list */}
        <div className="jobs-sidebar">
          <div className="sidebar-header">
            <h2>Jobs</h2>
            <button 
              className="icon-button"
              onClick={startCreateJob}
              title="Add New Job"
            >
              <FiPlus />
            </button>
          </div>
          
          {jobs.length === 0 && !loading ? (
            <div className="no-jobs-message">
              <p>No jobs yet. Click the + button to create your first job.</p>
            </div>
          ) : (
            <ul className="jobs-list">
              {jobs.map(job => (
                <li 
                  key={job.id}
                  className={selectedJob === job.id ? 'active' : ''}
                  onClick={() => {
                    setSelectedJob(job.id);
                    setCreateMode(false);
                    setStep(1);
                  }}
                >
                  <div className="job-list-item">
                    <span className="job-list-title">{job.title}</span>
                    <span className="job-list-summary">{job.summary}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        
        {/* Main content area */}
        <div className="main-content">
          {loading ? (
            <div className="loading-spinner">
              <FiLoader className="spinner" />
              <p>Loading...</p>
            </div>
          ) : createMode ? (
            <div className="create-job-container">
              {step === 1 ? (
                <>
                  <h2>Create New Job</h2>
                  <p className="step-indicator">Step 1 of 2: Enter Job Details</p>
                  
                  <div className="form-group">
                    <label htmlFor="jobTitle">Job Title</label>
                    <input
                      type="text"
                      id="jobTitle"
                      className="form-control"
                      placeholder="e.g. Senior Software Engineer"
                      value={newJobTitle}
                      onChange={(e) => setNewJobTitle(e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="jobDescription">Job Description</label>
                    <textarea
                      id="jobDescription"
                      className="form-control"
                      placeholder="Paste the full job description here..."
                      rows={12}
                      value={newJobDescription}
                      onChange={(e) => setNewJobDescription(e.target.value)}
                    />
                  </div>
                  
                  <div className="button-group">
                    <button 
                      className="secondary-button"
                      onClick={cancelCreateJob}
                    >
                      Cancel
                    </button>
                    <button 
                      className="primary-button"
                      onClick={createJob}
                      disabled={!newJobTitle.trim() || !newJobDescription.trim()}
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2>Upload Resumes</h2>
                  <p className="step-indicator">Step 2 of 2: Upload Candidate Resumes</p>
                  
                  <div 
                    className={`upload-area ${isDragging ? 'dragging' : ''}`}
                    onClick={() => document.getElementById("resumeFileInput").click()}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <FiUpload className="upload-icon" />
                    <p>Drag and drop candidate resumes here or click to browse</p>
                    <p className="upload-formats">PDF files only (.pdf)</p>
                    <input
                      type="file"
                      id="resumeFileInput"
                      style={{ display: "none" }}
                      onChange={handleFileUpload}
                      accept=".pdf"
                      multiple
                      disabled={uploadingResumes}
                    />
                  </div>
                  
                  {uploadingResumes && (
                    <div className="upload-progress">
                      <FiLoader className="spinner" />
                      <p>Uploading resumes...</p>
                    </div>
                  )}
                  
                  <div className="skip-message">
                    <p>You can also skip this step and upload resumes later.</p>
                  </div>
                  
                  <div className="button-group">
                    <button 
                      className="secondary-button"
                      onClick={() => {
                        setCreateMode(false);
                        setStep(1);
                        fetchJobDetails(selectedJob);
                      }}
                    >
                      Skip for Now
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : !selectedJob ? (
            <div className="no-selection-message">
              <h2>Welcome to {config_names.projectName}</h2>
              <p>Select a job from the sidebar or create a new one to get started.</p>
              <button className="primary-button" onClick={startCreateJob}>
                <FiPlus /> Create New Job
              </button>
            </div>
          ) : jobDetails ? (
            <div className="job-details-container">
              <div className="job-details-header">
                <div className="job-title-section">
                  <h2>{jobDetails.title}</h2>
                  <p className="job-created-at">Created {jobDetails.created_at}</p>
                </div>
                <div className="job-actions">
                  <button 
                    className="secondary-button"
                    onClick={() => document.getElementById("addResumesInput").click()}
                  >
                    <FiFileText /> Add Resumes
                    <input
                      type="file"
                      id="addResumesInput"
                      style={{ display: "none" }}
                      onChange={handleFileUpload}
                      accept=".pdf"
                      multiple
                    />
                  </button>
                  <button 
                    className="danger-button"
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete the job "${jobDetails.title}"?`)) {
                        deleteJob(jobDetails.id);
                      }
                    }}
                  >
                    <FiTrash2 /> Delete Job
                  </button>
                </div>
              </div>
              
              <div className="job-description-section">
                <h3>Job Description</h3>
                <div className="job-description-content">
                  {jobDetails.description.split('\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>
              
              <div className="resumes-section">
                <h3>Candidate Resumes</h3>
                
                {jobDetails.resumes.length === 0 ? (
                  <div className="no-resumes-message">
                    <p>No resumes uploaded yet. Upload resumes to see matches.</p>
                    <div 
                      className={`upload-area ${isDragging ? 'dragging' : ''}`}
                      onClick={() => document.getElementById("addResumesInput").click()}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <FiUpload className="upload-icon" />
                      <p>Drag and drop candidate resumes here or click to browse</p>
                      <p className="upload-formats">PDF files only (.pdf)</p>
                    </div>
                  </div>
                ) : (
                  <div className="resumes-list">
                    {jobDetails.resumes.map(resume => {
                      const resumeName = resume.original_filename || resume.filename || "Resume";
                      const candidateName = normalizeCandidateName(resume.candidate_name);
                      const candidateLabel = (candidateName || resumeName).replace(/\.pdf$/i, "");
                      const matchedSkills = normalizeSkillList(resume.matched_skills);
                      const missingSkills = normalizeSkillList(resume.missing_skills);
                      const safeSummary = toSingleSentence(resume.match_summary);
                      const statusValue = resume.status || "unknown";
                      const statusClass = statusValue.startsWith("error") ? "error" : statusValue;
                      return (
                      <div className="resume-card" key={resume.id}>
                        <div 
                          className={`resume-info ${resume.status === "completed" ? "clickable" : ""}`}
                          onClick={() => {
                            if (resume.status === 'completed') {
                              viewResume(resume.id);
                            }
                          }}
                        >
                          <div className="resume-header">
                            <div className="resume-icon-name">
                              <FiFile className="resume-icon" />
                              <span className="resume-name">{candidateLabel}</span>
                            </div>
                            <div className="resume-status-wrapper">
                              <span className={`resume-status status-${statusClass}`}>
                                {statusClass}
                              </span>
                            </div>
                          </div>
                          
                          {resume.status === 'completed' && (
                            <>
                              <div className="match-percentage-bar">
                                <div 
                                  className="match-percentage-fill" 
                                  style={{ 
                                    width: `${resume.match_percentage}%`,
                                    backgroundColor: resume.match_percentage > 80 ? 'var(--success-color)' : 
                                                    resume.match_percentage > 60 ? 'var(--primary-color)' : 
                                                    resume.match_percentage > 40 ? 'var(--warning-color)' : 
                                                    'var(--danger-color)'
                                  }}
                                ></div>
                              </div>
                              <span className="match-percentage-text">{resume.match_percentage}% Match</span>
                              <div className="match-summary">
                                {safeSummary}
                              </div>
                              {(matchedSkills.length > 0 || missingSkills.length > 0) && (
                                <div className="reason-chips">
                                  {matchedSkills.length > 0 && (
                                    <span className="reason-chip matched">
                                      Matched: {matchedSkills.join(", ")}
                                    </span>
                                  )}
                                  {missingSkills.length > 0 && (
                                    <span className="reason-chip missing">
                                      Missing: {missingSkills.join(", ")}
                                    </span>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          
                          {resume.status === 'processing' && (
                            <div className="processing-indicator">
                              <FiLoader className="spinner" />
                              <span>Analyzing resume...</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="resume-actions">
                          {resume.status === 'completed' && (
                            <button 
                              className="icon-button view-button" 
                              onClick={() => viewResume(resume.id)}
                              title="View Details"
                            >
                              <FiUser />
                            </button>
                          )}
                          <button 
                            className="icon-button delete-button" 
                            onClick={() => {
                              if (window.confirm("Are you sure you want to delete this resume?")) {
                                deleteResume(resume.id);
                              }
                            }}
                            title="Delete Resume"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="loading-spinner">
              <FiLoader className="spinner" />
              <p>Loading job details...</p>
            </div>
          )}
        </div>
      </div>
      
      {showToast && (
        <div className="toast-message">
          <FiCheckCircle />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
}

export default Overview;
